/**
 * @fileoverview BrandJsonLoader for loading modules based on dynamic brand JSON configuration.
 * 
 * This loader handles modules that depend on dynamic brand configuration loaded from
 * external JSON sources, enabling runtime customization and feature toggling based
 * on brand-specific settings and capabilities.
 * 
 * @author Roshan Jose
 * @version 2.0.0
 * @since 2025
 */

import { RouterHelper } from "../router-helper";
import { getBrandDetailsFromBrandJson } from "../../util/share";
import { IBrandJsonLoader, ModuleLoaderStatus } from "../interfaces/module-loader.interfaces";

/**
 * BrandJsonLoader manages dynamic brand configuration and feature-based module loading.
 * 
 * This loader is responsible for:
 * - Fetching dynamic brand configuration from external JSON sources
 * - Caching brand JSON data for performance optimization
 * - Loading modules based on brand-specific feature flags and settings
 * - Providing runtime customization capabilities
 * - Supporting dynamic feature toggling per brand
 * 
 * The BrandJsonLoader follows the singleton pattern and implements the IBrandJsonLoader
 * interface for consistent behavior across the router system.
 * 
 * Key Capabilities:
 * - Dynamic brand configuration fetching via getBrandDetailsFromBrandJson
 * - Intelligent caching to prevent redundant API calls
 * - Feature-based module loading based on JSON configuration
 * - Runtime customization and feature toggling
 * - Graceful handling of configuration loading failures
 * 
 * Use Cases:
 * - A/B testing with different feature sets per brand
 * - Gradual feature rollouts to specific brands
 * - Brand-specific customizations without code changes
 * - Dynamic module loading based on business rules
 * - Runtime configuration management
 * 
 * Features:
 * - Singleton pattern for global access
 * - Dynamic configuration fetching and caching
 * - Secure module loading via RouterHelper
 * - Comprehensive error handling and logging
 * - Extensible architecture for new features
 * 
 * @class BrandJsonLoader
 * @implements {IBrandJsonLoader}
 */
export class BrandJsonLoader implements IBrandJsonLoader {
    /** Singleton instance of the BrandJsonLoader */
    private static instance: BrandJsonLoader;
    
    /** Cached brand JSON configuration data */
    private brandJson: any;
    
    /** Current concept ID detected from the DOM */
    private conceptId: string = '';
    
    /** RouterHelper instance for secure module loading operations */
    private readonly routerHelper: RouterHelper = RouterHelper.getInstance();
    
    /** Current status and state of the BrandJsonLoader */
    private readonly status: ModuleLoaderStatus = {
        isInitialized: false,
        loadedModules: [],
        failedModules: [],
        isLoading: false
    };
    
    /**
     * Gets the singleton instance of BrandJsonLoader.
     * Creates a new instance if one doesn't exist.
     * @returns The singleton BrandJsonLoader instance
     */
    static getInstance(): BrandJsonLoader {
        this.instance ??= new BrandJsonLoader();
        return this.instance;
    }    
    
    /**
     * Initializes the BrandJsonLoader by detecting the concept ID.
     * 
     * Retrieves the concept ID from the DOM element 'conceptCode' which
     * will be used to fetch the appropriate brand configuration JSON.
     * 
     * @returns Promise that resolves when initialization is complete
     * @throws {Error} When concept ID detection or initialization fails
     */
    async init(): Promise<void> {
        try {
            this.conceptId = (document.getElementById('conceptCode') as HTMLInputElement)?.value ?? "";
            this.status.isInitialized = true;
        } catch (error) {
            this.status.lastError = error instanceof Error ? error.message : String(error);
            throw error;
        }
    }

    /**
     * Returns the human-readable name of this loader.
     * Used for logging, debugging, and status reporting.
     * @returns The loader's display name
     */
    getName(): string {
        return 'BrandJsonLoader';
    }

    /**
     * Returns a copy of the current loader status.
     * Provides real-time information about the loader's state.
     * @returns Current loader status information
     */
    getStatus(): ModuleLoaderStatus {
        return { ...this.status };
    }

    /**
     * Loads all brand JSON modules (delegates to brandJsonModules).
     * 
     * This is the main entry point for loading brand JSON modules,
     * implementing the IModuleLoader interface contract.
     * 
     * @returns Promise that resolves when all brand JSON modules are loaded
     */
    async loadModules(): Promise<void> {
        return this.brandJsonModules();
    }

    /**
     * Retrieves the brand-specific JSON configuration with intelligent caching.
     * 
     * Fetches brand configuration from external sources using the concept ID.
     * Implements caching to prevent redundant API calls and improve performance.
     * 
     * The brand JSON typically contains:
     * - Feature flags and toggles
     * - Brand-specific settings and customizations
     * - Module loading configurations
     * - A/B testing parameters
     * - Business rule configurations
     * 
     * @returns Promise that resolves to the brand JSON configuration
     * @throws {Error} When JSON fetching fails
     */
    async getBrandJson(): Promise<any> {
        if(this.brandJson) return this.brandJson;
        this.brandJson = await getBrandDetailsFromBrandJson(this.conceptId);
        return this.brandJson;
    }

    /**
     * Loads modules based on the brand JSON configuration.
     * 
     * This method orchestrates the dynamic loading of modules based on the
     * brand configuration fetched from external sources. It enables runtime
     * customization and feature toggling without code changes.
     * 
     * Process:
     * 1. Fetches brand JSON configuration via getBrandJson()
     * 2. Validates that configuration data was successfully loaded
     * 3. Delegates to loadBrandSpecificModules() for feature-based loading
     * 4. Updates loader status and provides comprehensive logging
     * 
     * The brand JSON configuration drives:
     * - Feature flag evaluation
     * - Conditional module loading
     * - A/B testing implementations
     * - Brand-specific customizations
     * - Business rule applications
     * 
     * @returns Promise that resolves when brand JSON modules are loaded
     * @throws {Error} When brand JSON loading or module loading fails
     */
    async brandJsonModules(): Promise<void> {
        this.status.isLoading = true;
        
        try {
            const brandJson = await this.getBrandJson();
            if(!brandJson) {
                this.routerHelper.routerConsoleLog('Brand JSON failed to load - no data returned', true, 'BrandJsonLoader');
                return;
            }
            this.routerHelper.routerConsoleLog('Brand JSON loaded successfully', false, 'BrandJsonLoader');
            this.status.loadedModules.push('brand-json');
            
            // Load brand-specific modules based on brandJson configuration
            await this.loadBrandSpecificModules(brandJson);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            this.status.failedModules.push('brand-json');
            this.status.lastError = errorMessage;
            this.routerHelper.routerConsoleLog(`Brand JSON failed to load - ${errorMessage}`, true, 'BrandJsonLoader');
            throw error;
        } finally {
            this.status.isLoading = false;
        }
    }
    
    /**
     * Loads brand-specific modules based on brand JSON configuration.
     * 
     * This private method implements the core logic for feature-based module loading.
     * It evaluates the brand JSON configuration and conditionally loads modules
     * based on feature flags, settings, and business rules.
     * 
     * Implementation Strategy:
     * - Evaluate feature flags in the brand JSON
     * - Check A/B testing configurations
     * - Apply business rule conditions
     * - Load appropriate modules using RouterHelper.loadModuleSafely
     * - Update loader status for each loaded module
     * 
     * Example Configuration Structure:
     * ```json
     * {
     *   "features": {
     *     "advancedAnalytics": true,
     *     "experimentalUI": false,
     *     "customCheckout": true
     *   },
     *   "modules": {
     *     "analytics": "advanced-analytics-module",
     *     "checkout": "custom-checkout-module"
     *   }
     * }
     * ```
     * 
     * @param brandJson - The brand configuration object containing feature flags and settings
     * @returns Promise that resolves when all configured modules are loaded
     * @private
     */
    private async loadBrandSpecificModules(brandJson: any): Promise<void> {
        // Implementation placeholder for feature-based module loading
        // This method should be expanded based on specific brand JSON structure
        // and business requirements for dynamic module loading
    }
}
